import { alpha, Box, ButtonGroup, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { AnnotationToolFeatureId } from '@verantos-dev/core-annotation-tool';
import { Button } from '@verantos-dev/shared-components';

import { METRICS_VIEW_OPTIONS } from './constants';
import { MetricDashboard } from './MetricDashboard';
import { MetricEncounterStats } from './MetricEncounterStats';
import { MetricHolidays } from './MetricHolidays';
import { MetricIRRScore } from './MetricIRRScore';
import { MetricSummary } from './MetricSummary';
import { useProjectMetrics } from './useProjectMetrics';

// Clipped by the ButtonGroup's rounded edge otherwise, so no button in this bar ever draws one.
const NO_OUTLINE = { outline: 'none' };

export const ProjectMetrics = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { selectedView, handleViewChange } = useProjectMetrics();
  // Alphas of theme.palette.secondary.main, not new colors — 0.2 matches containedSecondary's
  // own hover tint (muiButtonThemeConfig.ts); 0.3 is one step darker for hover-while-selected.
  const selectedBackground = alpha(theme.palette.secondary.main, 0.2);
  const selectedHoverBackground = alpha(theme.palette.secondary.main, 0.3);
  // containedSecondary's own :hover/:focus/:active repaint color; restating this pin at
  // matching specificity in each of those states is what makes it actually stick.
  const pinnedTextColor = { color: theme.palette.primary.main };
  const selectedRestFill = { backgroundColor: selectedBackground, ...pinnedTextColor };
  const selectedHoverFill = { backgroundColor: selectedHoverBackground, ...pinnedTextColor };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
      <Box>
        <ButtonGroup orientation="horizontal" disableElevation>
          {METRICS_VIEW_OPTIONS.map((option) => {
            const isSelected = option.id === selectedView;
            return (
              <Button
                key={option.id}
                id={`project-metrics-view-${option.id}`}
                displayLabel={formatMessage({ id: option.labelId })}
                btnType="secondary"
                sx={{
                  flex: 'none', // content-width, not equal-split — long labels wrapped otherwise
                  whiteSpace: 'nowrap',
                  borderRadius: 0, // square; only the ButtonGroup rounds its own outer edge
                  textTransform: 'none', // theme's typography has no `button` variant override
                  borderColor: theme.palette.secondary.main, // not containedSecondary's own hardcoded border
                  ...pinnedTextColor,
                  ...NO_OUTLINE,
                  '&:focus': { ...NO_OUTLINE },
                  '&:focus-visible': { ...NO_OUTLINE },
                  ...(isSelected && {
                    ...selectedRestFill,
                    '&:hover': selectedHoverFill,
                    '&:focus': { ...selectedRestFill, ...NO_OUTLINE, '&:hover': selectedHoverFill },
                    '&:active': selectedHoverFill,
                  }),
                }}
                onClickHandler={() => handleViewChange(option.id)}
              />
            );
          })}
        </ButtonGroup>
      </Box>
      {selectedView === AnnotationToolFeatureId.metricsDashboard && (
        <MetricDashboard hideProjectSelector />
      )}
      {selectedView === AnnotationToolFeatureId.metricsSummary && (
        <MetricSummary hideProjectSelector />
      )}
      {selectedView === AnnotationToolFeatureId.metricsEncounterStats && (
        <MetricEncounterStats hideProjectSelector />
      )}
      {selectedView === AnnotationToolFeatureId.metricsIRRScore && (
        <MetricIRRScore hideProjectSelector />
      )}
      {selectedView === AnnotationToolFeatureId.metricsHolidays && (
        <MetricHolidays hideProjectSelector />
      )}
    </Box>
  );
};
